import {
  Alert,
  Button as AmbrosiaButton,
  AnchorButton,
  Card,
  InputGroup,
  Modal,
} from '@payaca/ambrosia';
import { clstx } from '@payaca/utilities/cvaMerge';
import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';

import PaymentMethodIcons from '@payaca/components/paymentMethodIcons/PaymentMethodIcons';
import SwitchField from '@payaca/components/switchField/SwitchField';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import AccountNameField from '../bacsFields/AccountNameField';
import AccountNumberField from '../bacsFields/AccountNumberField';
import SortCodeField from '../bacsFields/SortCodeField';

import { get } from 'lodash-es';

import { actions as usersActions } from '@/api/users';
import { useBusinessAccountForm } from './useBusinessAccountForm';
import { useGettingPaidFormValidators } from '@/hooks/useCompanyInfoValidators';
import SettingsSaveButton from './SettingsSaveButton';
import { useUserHasPermission } from '@/hooks/usePermissions';
import { useAccount } from '@/api/rest/users/useAccount';
import { useRegion } from '@/api/rest/users/useRegion';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';

type Props = {
  readOnly: boolean;
};

type FormState = {
  allowStripePaymentDefault: boolean;
  allowBankTransferPaymentDefault: boolean;
  accountName: string;
  sortCode: string;
  accountNumber: string;
  externalPaymentRedirectUrl: string;
};

const STRIPE_CLIENT_ID = import.meta.env.VITE_STRIPE_CLIENT_ID;

const CompanySettingsPayments: FC<Props> = ({
  readOnly,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const [showStripeModal, setShowStripeModal] = useState(false);

  const account = useAccount();
  const region = useRegion();

  const canManageExternalPaymentRedirect = useUserHasPermission({
    permissions: [AccountsPermissions.MANAGE_EXTERNAL_PAYMENT_REDIRECT],
  });

  const hasBankDetails =
    !!account.sortCodeRaw &&
    !!account.accountNameRaw &&
    !!account.accountNumberRaw;

  const initialState: FormState = {
    // A payment default cannot apply without the thing it depends on. Normalise
    // here rather than in an effect: correcting it after mount would mark a
    // freshly-opened form dirty, enabling Save and arming the unsaved-changes
    // guard with nothing actually changed.
    allowStripePaymentDefault: !!(
      account.connectedToStripe && account.allowStripePaymentDefault
    ),
    allowBankTransferPaymentDefault: !!(
      hasBankDetails && account.allowBankTransferPaymentDefault
    ),
    accountName: account.accountNameRaw ?? '',
    sortCode: account.sortCodeRaw ?? '',
    accountNumber: account.accountNumberRaw ?? '',
    externalPaymentRedirectUrl: account.externalPaymentRedirectUrl ?? '',
  };

  const fieldValidators = useGettingPaidFormValidators(region);

  const {
    formState,
    updateFormFields,
    formValidationResult,
    saveAndFlush,
    isValid,
    isDirty,
    isSaving,
  } = useBusinessAccountForm<FormState>(initialState, fieldValidators);

  const isConnectedToStripe = useMemo(
    () => account && account.connectedToStripe,
    [account]
  );

  const interval = useRef<any>(null);

  useEffect(() => {
    return () => clearInterval(interval.current);
  }, []);

  const setCheckStripeStatusInterval = () => {
    clearInterval(interval.current);
    interval.current = setInterval(() => checkStripeStatus(), 3000);
  };

  // Dismissing the modal ends the connection attempt, so stop polling — the poll
  // re-fetches the profile and re-dirties the form every 3 seconds otherwise.
  const closeStripeModal = () => {
    clearInterval(interval.current);
    setShowStripeModal(false);
  };

  const checkStripeStatus = () => {
    dispatch(
      usersActions.getProfile((err: any, response: any) => {
        const connectedToStripe = get(response, [
          'accounts',
          0,
          'connectedToStripe',
        ]);

        // Only write on success. Writing `false` on every not-yet-connected
        // tick banked a phantom change per poll — `updateFormFields` counts
        // dirtiness unconditionally — leaving Save enabled and the
        // unsaved-changes guard armed after dismissing the modal.
        if (connectedToStripe) {
          updateFormFields({
            allowStripePaymentDefault: true,
          });
          clearInterval(interval.current);
        }
      })
    );
  };

  const onAllowStripePaymentDefaultChange = useCallback(
    (allowStripePaymentDefault: boolean) => {
      if (isConnectedToStripe) {
        updateFormFields({
          allowStripePaymentDefault: allowStripePaymentDefault,
        });
      } else {
        setCheckStripeStatusInterval();
        if (allowStripePaymentDefault) {
          setShowStripeModal(true);
        } else {
          updateFormFields({
            allowStripePaymentDefault: false,
          });
        }
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'setCheckStripeStatusInterval'
    [isConnectedToStripe, updateFormFields]
  );

  const hasExternalPaymentRedirect = !!formState.externalPaymentRedirectUrl;

  const allowBankTransferPaymentDefaultAvailable = useMemo(() => {
    return (
      !!formState.sortCode &&
      !!formState.accountName &&
      !!formState.accountNumber
    );
  }, [formState]);

  useEffect(() => {
    if (
      !allowBankTransferPaymentDefaultAvailable &&
      formState.allowBankTransferPaymentDefault
    ) {
      updateFormFields({
        allowBankTransferPaymentDefault: false,
      });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'updateFormFields'
  }, [allowBankTransferPaymentDefaultAvailable]);

  useEffect(() => {
    if (!account.connectedToStripe && formState.allowStripePaymentDefault) {
      updateFormFields({
        allowStripePaymentDefault: false,
      });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'updateFormFields'
  }, [account?.connectedToStripe]);

  return (
    <>
      <Card sizeVariant="lg">
        <Card.Body className="flex flex-col gap-2">
          <h2>Payment method defaults</h2>
          {hasExternalPaymentRedirect && (
            <Alert colour="yellow" className="mt-2 mb-3">
              Payment toggles are unavailable when an external payment redirect
              is configured.
            </Alert>
          )}
          <SwitchField
            name="allowStripePaymentDefault"
            value={formState.allowStripePaymentDefault}
            onChange={(value) => {
              onAllowStripePaymentDefaultChange(
                value.allowStripePaymentDefault
              );
            }}
            label={
              <span
                className={clstx(
                  'flex flex-row flex-wrap gap-2',
                  hasExternalPaymentRedirect && 'opacity-50'
                )}
              >
                <span>Payments with Stripe </span>
                <PaymentMethodIcons />{' '}
                <a
                  href="https://stripe.com/gb/guides/payment-methods-guide"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  + many more
                </a>
              </span>
            }
            isDisabled={readOnly || hasExternalPaymentRedirect}
          />
          <div className="bank-transfer-switch-field-container flex space-x-3.5 items-center">
            <SwitchField
              name="allowBankTransferPaymentDefault"
              value={formState.allowBankTransferPaymentDefault}
              onChange={updateFormFields}
              label={
                <span
                  className={clstx(hasExternalPaymentRedirect && 'opacity-50')}
                >
                  Direct bank transfer
                </span>
              }
              isDisabled={
                readOnly ||
                !allowBankTransferPaymentDefaultAvailable ||
                hasExternalPaymentRedirect
              }
            />
            {!allowBankTransferPaymentDefaultAvailable &&
              !hasExternalPaymentRedirect && (
                <small className="enter-bank-details-prompt">
                  Enter bank details below to enable this option
                </small>
              )}
          </div>
        </Card.Body>
      </Card>
      <Card sizeVariant="lg">
        <Card.Body className="flex flex-col gap-2">
          <h2>External payment redirect</h2>
          <p className="supporting-body">
            Customers will be redirected to this URL to pay for invoices,
            instead of the normal Stripe and direct bank payment options.
          </p>

          <ValidatedFieldWrapper
            validationResult={formValidationResult.externalPaymentRedirectUrl}
          >
            <InputGroup className="shadow-none">
              <InputGroup.Input
                name="externalPaymentRedirectUrl"
                value={formState.externalPaymentRedirectUrl}
                onChange={(value) =>
                  updateFormFields({ externalPaymentRedirectUrl: value })
                }
                disabled={readOnly || !canManageExternalPaymentRedirect}
              />
              <InputGroup.Button
                variant="secondary"
                onClick={() =>
                  updateFormFields({ externalPaymentRedirectUrl: '' })
                }
                disabled={
                  readOnly ||
                  !canManageExternalPaymentRedirect ||
                  !hasExternalPaymentRedirect
                }
              >
                Clear
              </InputGroup.Button>
            </InputGroup>
          </ValidatedFieldWrapper>
        </Card.Body>
      </Card>
      <Card sizeVariant="lg">
        <Card.Body className="flex flex-col gap-2">
          <h2>Bank details for direct bank transfer</h2>
          <Alert colour="yellow" className="mt-2 mb-3">
            <p>
              We recommend using <strong>Stripe</strong> even for bank payments,
              as it allows us to mark invoices as paid automatically.
            </p>
          </Alert>
          <AccountNameField
            name="accountName"
            value={formState.accountName}
            onChange={updateFormFields}
            isReadOnly={readOnly}
          />
          <ValidatedFieldWrapper
            validationResult={formValidationResult.sortCode}
          >
            <SortCodeField
              name="sortCode"
              value={formState.sortCode}
              onChange={updateFormFields}
              isReadOnly={readOnly}
            />
          </ValidatedFieldWrapper>
          <ValidatedFieldWrapper
            validationResult={formValidationResult.accountNumber}
          >
            <AccountNumberField
              name="accountNumber"
              value={formState.accountNumber}
              onChange={updateFormFields}
              isReadOnly={readOnly}
            />
          </ValidatedFieldWrapper>
        </Card.Body>
      </Card>
      {!readOnly && (
        <SettingsSaveButton
          isDirty={isDirty}
          isSaving={isSaving}
          isValid={isValid}
          onSave={saveAndFlush}
        />
      )}
      <Modal
        isOpen={showStripeModal}
        title="Accept card payments with Stripe"
        onClose={closeStripeModal}
        size="sm"
      >
        <Modal.Body>
          <div className="flex flex-col gap-2.5">
            <p>
              Stripe is a secure payment platform, trusted by over a million
              businesses all over the world.
            </p>
            <p>
              If you already have a Stripe account you can connect to it or if
              you don't you can create one.
            </p>
            <ul className="list-none space-y-1 p-0">
              <li>
                Make it easy for customers to pay fast with a button on your
                quotes and invoices.
              </li>
              <li>Fast, easy setup</li>
              <li>
                Fees for European cards are 1.4% +20p, you can find out more{' '}
                <a
                  href="https://stripe.com/gb/pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <AmbrosiaButton variant="secondary" onClick={closeStripeModal}>
              No thanks
            </AmbrosiaButton>
            <AnchorButton
              href={`https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${STRIPE_CLIENT_ID}&scope=read_write&redirect_uri=${window.location.origin}/enableCustomerPayments`}
            >
              Connect with Stripe
            </AnchorButton>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CompanySettingsPayments;
